<template>
	<v-card class="elevation-0 home-page page">
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<div class="home-page__wrap">
				<FeaturedPost :post="this.normalizedFeaturedPost" />
			</div>
			<div v-if="this.filteredPosts.length !== 0">
				<filter-chip-button
					:isButtonActive="isButtonActive"
					:getAndCallDynamicFunction="getAndCallDynamicFunction"
					:normalizedFeedFilterChips="normalizedFeedFilterChips"
					:clearFilter="clearFilter"
				/>
				<PostFilterModal
					v-on:closeModal="closeModal"
					:show="showModal"
					:selected-button="selectedButton"
					:current-active-button="this.currentActiveButton"
					:currentActiveButtonTitle="currentActiveButtonTitle"
					@applySelection="applySelection"
					@setItems="setSelectedItems"
				/>
			</div>
			<div
				v-if="this.filteredPosts.length !== 0"
				class="post__fake-btn-modal-container mt-5"
				@click="openCreatePostModal"
			>
				<div>
					<Avatar
						class="mr-4 post-preview-card__avatar"
						:size="30"
						:avatar="profile.user_logo"
					/>
					What’s on your mind?
				</div>

				<div>
					<img :src="penIcon" alt="penIcon" loading="lazy" />
				</div>
			</div>
		</div>

		<div class="d-flex">
			<v-skeleton-loader
				v-if="isLoading"
				v-bind="attrs"
				width="600"
				type="list-item-avatar, list-item-three-line, card-heading, actions"
			></v-skeleton-loader>
			<div v-else style="width: 100%">
				<div v-if="this.filteredPosts.length === 0" class="mt-5">
					<NewsFeedEmpty
						title="Your News Feed is empty at the moment."
						description="Check back later or create a new post."
					/>
				</div>

				<div
					v-else
					v-for="item in filteredPosts"
					:key="item.thread_id"
					class="mb-1"
					style="margin: 0 !important"
				>
					<PostPreview
						class="post-preview"
						:post="item"
						:displayViews="displayViews"
						:displayLikes="displayLikes"
						:enableLikesButton="enableLikesButton"
						:displayCommentsCount="displayCommentsCount"
						:thread_menu_id="+$route.query.thread_menu_id"
					/>
				</div>
			</div>
		</div>

		<div
			class="d-flex my-9 justify-center home-page__pagination"
			v-if="!isLoading && posts.length > 0"
		>
			<Pagination
				:page="filters.page"
				:pageCount="+this.pageCount"
				v-on:pageChange="onPageChange"
			/>
		</div>

		<div v-if="show">
			<CreatePostModal v-on:closeModal="closeCreatePostModal" :show="show" />
		</div>

		<v-btn
			v-if="isAllowPostCreating"
			v-show="isShowCreatePostButton"
			@click="openCreatePostModal"
			bottom
			right
			:class="{ 'add-post-button': true, 'add-post-button-playerOn': currentPodcast }"
			fixed
			fab
		>
			<v-icon>mdi-plus</v-icon>
		</v-btn>

		<ScrollToTopButton />
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import FeaturedPost from '@/components/Post/PostFeaturedPreview'
import PostPreview from '@/components/Post/PostPreview'
import NewsFeedEmpty from '@/components/Post/NewsFeedEmpty'
import searchIcon from '@/assets/images/search.svg'
import penIcon from '@/assets/images/icon-pen.svg'
import Pagination from '@/components/Pagination.vue'

import { normalizeFeaturedPost } from '@/utils/helpers'
import PostFilterModal from '@/components/Modals/PostFilterModal'
import Avatar from '@/components/Avatar'
import CreatePostModal from '@/components/Modals/CreatePostModal'
import Block from '@/mixins/Block.js'
import Dwh from '@/mixins/Dwh'
import PageBlocked from '@/components/PageBlocked'
import ScrollToTopButton from '@/components/ScrollToTopButton'
import _ from 'lodash'
import FilterChipButton from '@/components/FilterChipButtons.vue'

const DEBOUNCE_TIMEOUT = 500

export default {
	name: 'HomePage',
	mixins: [Block, Dwh],
	components: {
		FilterChipButton,
		PostFilterModal,
		PostPreview,
		FeaturedPost,
		NewsFeedEmpty,
		Avatar,
		CreatePostModal,
		PageBlocked,
		Pagination,
		ScrollToTopButton,
	},
	beforeMount() {
		this.setSearching(this.filterSearch)
		this.clearButtonHandler()
	},

	methods: {
		...mapActions('post', ['fetchPosts', 'getFeaturedPost']),
		...mapActions('settings', ['fetchChapters', 'getAppCoreSettings']),
		...mapActions('tag', ['setDefaultStructure']),
		...mapActions('app', ['setSearching']),

		isNumeric(value) {
			return _.isNumber(value) || (!_.isNaN(value) && !_.isNaN(parseFloat(value)))
		},
		scrollToTop() {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
		},
		checkScreenWidth() {
			this.isShowCreatePostButton = window.innerWidth >= 1264
		},
		onPageChange(page) {
			if (page !== this.filters.page) {
				this.filters.page = page
				const params = {
					filter: this.filters.filter,
					chapter_id: this.filters.groups,
					tag_ids: this.filters.tags,
					search: this.filters.search,
					user_id: this.filters.userId,
					thread_menu_id: this.$route.query.thread_menu_id,
					page: page,
				}
				const queryString = this.formatFilters(params)
				this.getNews(queryString)
			}
		},

		formatFilters(filters) {
			const { filter, chapter_id, tag_ids, search, user_id, page, thread_menu_id } = filters

			const tagIds = tag_ids.reduce((prev, next) => {
				return `${prev}&tag_ids[]=${next}`
			}, '')

			const groupIds = chapter_id.reduce((prev, next) => {
				return `${prev}&chapter_id[]=${next}`
			}, '')

			let queryString = `user_id=${user_id}&filter=${filter}${tagIds}${groupIds}&page=${page}&thread_menu_id=${thread_menu_id}`

			if (search !== undefined && search.length > 0) {
				queryString += `&search=${search}`
			}

			return queryString
		},

		filterSpecifiedValues(list, filterValues) {
			return list.filter((item) => !filterValues.includes(item))
		},

		async getNews(additionalQueryParams = '') {
			await this.fetchPosts(additionalQueryParams)
		},

		clearFilter(name) {
			switch (name) {
				case 'chapters':
					this.selectedChapters = []
					this.filters.groups = []

					if (Array.isArray(this.profile.chapters)) {
						this.profile.chapters = this.profile.chapters.map((el) => ({
							...el,
							checked: false,
						}))
					}
					break
				case 'tags':
					this.selectedTags = []
					this.filters.tags = []
					this.setDefaultStructure()
					break
				case 'search':
					this.filters.search = ''
					break
				case 'myposts':
					this.filters.userId = null
					break
				case 'admin':
					this.filters.filter = null
					break
				default:
					console.warn(`Неизвестное имя фильтра: ${name}`)
			}

			this.selectedButton = this.selectedButton.filter((button) => button !== name)

			if (this.selectedButton.length === 0) {
				this.selectedButton.push('clear')
			}

			this.showModal = false

			const params = {
				filter: this.filters.filter,
				chapter_id: this.filters.groups,
				tag_ids: this.filters.tags,
				user_id: this.filters.userId,
				search: this.filters.search,
				page: 1,
			}

			const queryString = this.formatFilters(params)
			this.getNews(queryString)
		},

		clearButtonHandler() {
			this.currentActiveButton = 'clear'
			this.selectedButton = ['clear']

			this.selectedChapters = []
			this.selectedTags = []
			if (Array.isArray(this.profile.chapters)) {
				this.profile.chapters = this.profile.chapters.map((el) => ({
					...el,
					checked: false,
				}))
			}
			this.setDefaultStructure()

			this.filters = {
				filter: null,
				groups: [],
				tags: [],
				userId: null,
				search: '',
				page: 1,
			}

			if (Array.isArray(this.profile.chapters)) {
				this.profile.chapters = this.profile.chapters.map((el) => ({
					...el,
					checked: false,
				}))
			}

			this.showModal = false

			const params = {
				filter: this.filters.filter,
				chapter_id: this.filters.groups,
				tag_ids: this.filters.tags,
				user_id: this.filters.userId,
				search: this.filters.search,
				page: this.filters.page,
			}

			const queryString = this.formatFilters(params)
			this.getNews(queryString)
		},

		adminButtonHandler() {
			this.currentActiveButton = 'admin'
			const mutuallyExclusiveButtons = ['clear', 'users']

			this.selectedButton = this.getFilteredChips(
				this.selectedButton,
				this.currentActiveButton,
				mutuallyExclusiveButtons
			)

			const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)

			this.filters = {
				...this.filters,
				filter: isHandleButtonActive ? 'admin-news' : null,
			}
		},
		usersButtonHandler() {
			this.currentActiveButton = 'users'
			const mutuallyExclusiveButtons = ['clear', 'admin']

			this.selectedButton = this.getFilteredChips(
				this.selectedButton,
				this.currentActiveButton,
				mutuallyExclusiveButtons
			)

			const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)

			this.filters = {
				...this.filters,
				filter: isHandleButtonActive ? 'user-news' : null,
			}
		},

		tagsButtonHandler() {
			this.currentActiveButton = 'tags'

			this.currentActiveButtonTitle = this.normalizedFeedFilterChips.find(
				(item) => item.name === this.currentActiveButton
			).content

			const mutuallyExclusiveButtons = ['clear']

			this.selectedButton = this.getFilteredChips(
				this.selectedButton,
				this.currentActiveButton,
				mutuallyExclusiveButtons
			)

			this.setActiveButtonTitle()

			const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)

			if (!isHandleButtonActive) {
				this.selectedTags = []
				this.filters = {
					...this.filters,
					tags: [],
				}
			}

			this.showModal = isHandleButtonActive
		},
		chaptersButtonHandler() {
			this.currentActiveButton = 'chapters'

			this.currentActiveButtonTitle = this.normalizedFeedFilterChips.find(
				(item) => item.name === this.currentActiveButton
			).content

			const mutuallyExclusiveButtons = ['clear']

			this.selectedButton = this.getFilteredChips(
				this.selectedButton,
				this.currentActiveButton,
				mutuallyExclusiveButtons
			)

			this.setActiveButtonTitle()

			const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)

			if (!isHandleButtonActive) {
				this.selectedChapters = []
				this.filters = {
					...this.filters,
					groups: [],
				}
			}

			this.showModal = isHandleButtonActive
		},
		mypostsButtonHandler() {
			this.currentActiveButton = 'myposts'
			const mutuallyExclusiveButtons = ['clear']

			this.selectedButton = this.getFilteredChips(
				this.selectedButton,
				this.currentActiveButton,
				mutuallyExclusiveButtons
			)

			const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)

			this.filters = {
				...this.filters,
				userId: isHandleButtonActive ? this.profile.user_id : null,
			}
		},

		getFilteredChips(selectedButtons, handleButtonName, mutuallyExclusiveButtons) {
			const isButtonPresentInlist = this.selectedButton.includes(handleButtonName)

			if (!isButtonPresentInlist) {
				return this.filterSpecifiedValues(
					[...this.selectedButton, handleButtonName],
					mutuallyExclusiveButtons
				)
			} else {
				return this.selectedButton
			}
		},

		getAndCallDynamicFunction(name) {
			let stringWithUnderscores = name.replace(/-/g, '_')
			const dynamicFunctionName = `${stringWithUnderscores}ButtonHandler`
			const dynamicFunction = this[dynamicFunctionName]
			if (typeof dynamicFunction === 'function') {
				dynamicFunction()
				this.sendTrigger({
					category: 'Content',
					action: 'click_filter_news_post',
					module: 'News Feed',
					title: name,
				})
			} else {
				console.error('Function not found')
			}
		},
		closeModal() {
			this.showModal = false

			const mutuallyExclusiveButtons = ['clear']

			switch (this.currentActiveButton) {
				case 'chapters':
					this.currentActiveButton = 'chapters'

					this.selectedChapters = []
					this.filters = {
						...this.filters,
						groups: [],
					}

					break
				case 'tags':
					this.currentActiveButton = 'tags'

					this.selectedTags = []
					this.filters = {
						...this.filters,
						tags: [],
					}

					break
			}

			this.selectedButton = this.getFilteredChips(
				this.selectedButton,
				this.currentActiveButton,
				mutuallyExclusiveButtons
			)
		},

		setSelectedItems(item) {
			const { listItem, selectedType, idColumnName = 'id' } = item

			this.setDynamicItems(listItem, selectedType, idColumnName)
		},
		setDynamicItems(item, selectedType, idColumnName = 'id') {
			if (!Array.isArray(this.selectedChips[selectedType])) {
				this.selectedChips[selectedType] = []
			}

			const itemId = item[idColumnName]

			const index = this.selectedChips[selectedType].indexOf(itemId)

			if (index !== -1) {
				this.selectedChips[selectedType].splice(index, 1)
			} else {
				this.selectedChips[selectedType].push(itemId)
			}

			this[selectedType] = [...this.selectedChips[selectedType]]
		},

		applySelection() {
			this.filters.groups = this.selectedChapters
			this.filters.tags = this.selectedTags
			this.showModal = false
		},

		filterSearch(value) {
			clearTimeout(this.timer)

			const params = {
				filter: this.filters.filter,
				chapter_id: this.filters.groups,
				tag_ids: this.filters.tags,
				search: value,
				user_id: this.filters.userId,
				thread_menu_id: this.$route.query.thread_menu_id,
				page: 1,
			}
			this.filters.page = 1
			const queryString = this.formatFilters(params)
			this.timer = setTimeout(() => {
				this.getNews(queryString)
			}, DEBOUNCE_TIMEOUT)
		},

		closeCreatePostModal() {
			this.show = false
		},

		openCreatePostModal() {
			this.show = true
		},
		isButtonActive(name) {
			return this.selectedButton.includes(name)
		},
		setActiveButtonTitle() {
			this.currentActiveButtonTitle = this.normalizedFeedFilterChips.find(
				(item) => item.name === this.currentActiveButton
			).content
		},
	},
	created() {
		let params = ''

		if (!this.isBlocked || this.isBlocked === null) {
			if (this.$route.query.thread_menu_id) {
				params = `thread_menu_id=${this.$route.query.thread_menu_id}&page=1&filter=null`
			}

			this.fetchPosts(params)
		}
	},

	computed: {
		...mapGetters('auth', ['isAuthenticated', 'profile']),
		...mapGetters('post', [
			'isLoading',
			'posts',
			'featuredPost',
			'isFeaturedPostLoading',
			'pageCount',
		]),
		...mapGetters('category', ['selectedCategory']),
		...mapGetters('tag', ['tags']),
		...mapGetters('settings', ['appCore']),
		...mapGetters('rss', ['currentPodcast']),
		needScroll() {
			return this.normalizedFeedFilterChips.length > 5
		},
		canScrollLeft() {
			return this.translateX < 0
		},

		isAllowPostCreating() {
			return this.isNumeric(this.$route.query.category_id)
		},
		normalizedFeaturedPost() {
			return normalizeFeaturedPost(this.featuredPost)
		},
		redirectBaseUrl() {
			if (!this.appCore) {
				return []
			}
			return this.appCore.appMenuNormalized.baseMenuItems.find(
				(item) => item.route.name === 'threads'
			)
		},
		normalizedFeedFilterChips() {
			if (!this.appCore) {
				return {}
			}

			const { chips = [] } =
				this.appCore.appMenuNormalized.baseMenuItems.find((item) => {
					const {
						route: { name, query: queryCurrentObjectParams },
					} = item

					if (name === 'threads') {
						const queryCurrentParams = _.cloneDeepWith(this.$route.query, (value) => {
							return _.isObject(value) ? undefined : Number(value)
						})
						const planeQueryCurrentObjectParams = _.cloneDeepWith(
							queryCurrentObjectParams,
							(value) => {
								return _.isObject(value) ? undefined : Number(value)
							}
						)

						return _.isEqualWith(
							queryCurrentParams,
							planeQueryCurrentObjectParams,
							(value1, value2) => {
								if (_.isObject(value1) && _.isObject(value2)) {
									return _.isEqual(_.sortBy(value1), _.sortBy(value2))
								}
							}
						)
					}
				}) || {}
			return chips
		},
		filteredPosts() {
			return this.posts.filter(
				(post) =>
					post.thread_type !== 4 ||
					(post.thread_type === 4 && post.poll.vote && post.poll.display_poll) ||
					(post.thread_type === 4 && !post.poll.vote)
			)
		},
		currentMenuItem() {
			if (this.appCore) {
				const regex = /:\/\/(.+)/
				const currentMenuItem = this.appCore.app_menu.find((item) => {
					const match = item.url.match(regex)
					const result = match ? match[1] : null
					return (
						result === 'threads' &&
						item.params.thread_menu_id === +this.$route.query.thread_menu_id
					)
				})
				return currentMenuItem
			}
			return null
		},
		displayViews() {
			if (this.currentMenuItem) {
				return !!this.currentMenuItem.params.display_views
			}
			return false
		},
		displayLikes() {
			if (this.currentMenuItem) {
				return !!this.currentMenuItem.params.display_likes
			}
			return false
		},
		enableLikesButton() {
			if (this.currentMenuItem) {
				return !!this.currentMenuItem.params.enable_likes_button
			}
			return false
		},
		displayCommentsCount() {
			if (this.currentMenuItem) {
				return !!this.currentMenuItem.params.display_comments_count
			}
			return false
		},
	},

	watch: {
		$route() {
			this.resetScroll()
			this.clearButtonHandler()
		},
		selectedCategory: {
			handler(val) {
				this.getFeaturedPost(val ?? 1)
			},
		},
		appCore: {
			handler(appCore) {
				if (!this.$route.query.thread_menu_id && appCore) {
					const { route } = this.redirectBaseUrl || {}

					this.$router.push({
						name: route.name,
						query: route.query,
					})
				}
			},
			immediate: true,
		},
		'$route.query.thread_menu_id': function () {
			let params = ''

			if (this.$route.query.thread_menu_id) {
				params = `thread_menu_id=${this.$route.query.thread_menu_id}&page=1&filter=null`
			}

			this.filters = {
				filter: null,
				groups: [],
				tags: [],
				userId: null,
				search: '',
			}
			this.selectedChapters = []
			this.selectedTags = []
			this.selectedButton = ['clear']

			this.fetchPosts(params)
		},

		'filters.userId': function (val) {
			const params = {
				filter: this.filters.filter,
				chapter_id: this.filters.groups,
				tag_ids: this.filters.tags,
				search: this.filters.search,
				user_id: val,
				thread_menu_id: this.$route.query.thread_menu_id,
				page: 1,
			}

			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.getNews(queryString)
		},
		'filters.filter': function (val) {
			const params = {
				filter: val,
				chapter_id: this.filters.groups,
				tag_ids: this.filters.tags,
				search: this.filters.search,
				user_id: this.filters.userId,
				thread_menu_id: this.$route.query.thread_menu_id,
				page: 1,
			}

			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.getNews(queryString)
		},
		'filters.groups': function (next, prev) {
			const isValuesEmptyOrEqual =
				(next.length <= 0 && prev.length <= 0) || _.isEqual(next, prev)

			if (isValuesEmptyOrEqual) {
				return false
			}

			const params = {
				filter: this.filters.filter,
				chapter_id: next,
				tag_ids: this.filters.tags,
				search: this.filters.search,
				user_id: this.filters.userId,
				thread_menu_id: this.$route.query.thread_menu_id,
				page: 1,
			}

			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.getNews(queryString)
		},
		'filters.tags': function (next, prev) {
			const isValuesEmptyOrEqual =
				(next.length <= 0 && prev.length <= 0) || _.isEqual(next, prev)
			if (isValuesEmptyOrEqual) {
				return false
			}

			const params = {
				filter: this.filters.filter,
				chapter_id: this.filters.groups,
				tag_ids: next,
				search: this.filters.search,
				user_id: this.filters.userId,
				thread_menu_id: this.$route.query.thread_menu_id,
				page: 1,
			}

			this.filters.page = 1
			const queryString = this.formatFilters(params)

			this.getNews(queryString)
		},

		selectedButton(cur) {
			if (cur.length === 0) {
				this.selectedButton = ['clear']
			}
		},
	},
	mounted() {
		window.addEventListener('resize', this.checkScreenWidth)
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.checkScreenWidth)
	},
	data() {
		return {
			scrollRightStop: false,
			maxTranslateX: 110,
			translateX: 0,
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			isShowCreatePostButton: true,
			viewAllPopularPosts: false,
			timer: 0,
			categoryId: 1,
			searchIcon,
			penIcon,
			show: false,

			filters: {
				filter: null,
				groups: [],
				tags: [],
				userId: null,
				page: 1,
				search: '',
			},

			selectedButton: ['clear'],
			selectedChips: [],
			currentActiveButton: '',
			currentActiveButtonTitle: '',
			showModal: false,
			selectedChapters: [],
			selectedTags: [],
			search: '',
			page: '',
			searchValue: '',
			chapters: '',
			pageBlockedName: 'threads',
		}
	},
}
</script>

<style lang="scss">
.post-preview {
	padding-top: 25px !important;
	padding-left: 2.5rem;
	padding-right: 2.5rem;
}

@media (min-width: 640px) {
	.post-preview {
		padding-left: 1.25rem;
		padding-right: 1.25rem;
	}
}

@media (min-width: 1024px) {
	.post-preview {
		padding-left: 0;
		padding-right: 0;
	}
}

.home-page {
	background: #f1f1f1 !important;

	&__divider {
		border-color: #000 !important;
		mix-blend-mode: normal;
		opacity: 0.1;
	}

	&__pagination {
		@media screen and (min-width: 1264px) {
			max-width: 600px;
		}

		.theme--light.v-pagination .v-pagination__item--active {
			color: #fff !important;
			background: var(--accent-color) !important;
			border-radius: 4px;
			height: 30px;
			width: 30px;
		}

		.v-pagination__item {
			font-size: 15px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-align: center;
			color: var(--accent-color) !important;
			background: none !important;
			box-shadow: none !important;
			white-space: nowrap;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.v-pagination__navigation {
			background: none !important;
			box-shadow: none !important;
		}
	}

	.post {
		&__search {
			background: white;
			height: 54px;
			border-radius: 10px;
			border: 1px solid rgb(230, 230, 230);
			max-width: 600px;
		}

		&__fake-btn-modal {
			&-container {
				display: flex;
				align-items: center;
				justify-content: space-between;
				height: 54px;
				background: rgb(255, 255, 255);
				border: 1px solid rgb(230, 230, 230);
				border-radius: 10px;
				padding: 20px 15px;
				max-width: 600px;
				cursor: pointer;

				color: rgb(160, 160, 160);
				font-size: 15px;
				font-family: Montserrat, sans-serif;
				font-weight: normal;
				letter-spacing: -0.43px;
			}
		}

		@media screen and (max-width: 1264px) {
			&__search {
				max-width: none;
				width: 100%;
			}
			&__fake-btn-modal-container {
				max-width: none;
				width: 100%;
			}
		}
	}
}

.add-post-button {
	right: 20% !important;

	&-playerOn {
		transform: translateY(-100px);

		@media (max-width: 710px) {
			transform: translateY(-150px);
		}

		@media (max-width: 530px) {
			transform: translateY(-200px);
		}
	}
}

@media screen and (min-width: 1264px) and (max-width: 1600px) {
	.add-post-button {
		right: 13% !important;
	}
}
</style>
